import React from 'react';

/**
 * Card Component
 * 
 * Production-grade card container with comprehensive features:
 * - Header section (title, actions)
 * - Footer section (actions, metadata)
 * - Body section (main content)
 * - Clickable (with hover effect)
 * - Hoverable (subtle lift on hover)
 * - Padding variants
 * - Border variants
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * Industry Pattern: Material Card, Stripe Dashboard Cards, Linear Cards
 * 
 * @example
 * ```tsx
 * <Card
 *   header={<h3>Test Results</h3>}
 *   footer={<Button>View Details</Button>}
 *   hoverable
 * >
 *   <p>Content goes here...</p>
 * </Card>
 * 
 * <Card clickable onClick={() => navigate('/details')}>
 *   <p>Clickable card</p>
 * </Card>
 * ```
 */

export interface CardProps {
  /** Header content (typically title, actions) */
  header?: React.ReactNode;
  /** Footer content (typically actions, metadata) */
  footer?: React.ReactNode;
  /** Body content */
  children: React.ReactNode;
  /** Make entire card clickable */
  clickable?: boolean;
  /** Click handler (only when clickable=true) */
  onClick?: () => void;
  /** Hover effect (subtle lift) */
  hoverable?: boolean;
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Border style */
  bordered?: boolean;
  /** Shadow style */
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const CardComponent: React.FC<CardProps> = ({
  header,
  footer,
  children,
  clickable = false,
  onClick,
  hoverable = false,
  padding = 'md',
  bordered = true,
  shadow = 'sm',
  className = '',
}) => {
  // Padding classes
  const paddingClasses = {
    none: 'p-0',
    sm: 'p-3',
    md: 'p-4 sm:p-5',
    lg: 'p-5 sm:p-6',
  };

  // Shadow classes
  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  };

  // Determine if the card is interactive
  const isInteractive = clickable && onClick;

  // Card element (button if clickable, div otherwise)
  const CardElement = isInteractive ? 'button' : 'div';

  return (
    <CardElement
      type={isInteractive ? 'button' : undefined}
      onClick={isInteractive ? onClick : undefined}
      className={`
        bg-white dark:bg-slate-800
        ${bordered ? 'border border-gray-200 dark:border-slate-700' : ''}
        rounded-lg
        ${shadowClasses[shadow]}
        transition-all
        duration-200
        ${isInteractive ? 'cursor-pointer text-left w-full' : ''}
        ${isInteractive || hoverable ? 'hover:shadow-md dark:hover:shadow-slate-900/50 hover:-translate-y-0.5' : ''}
        ${isInteractive ? 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-blue-400 dark:focus:ring-offset-slate-900' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Header Section */}
      {header && (
        <div
          className={`
            ${paddingClasses[padding]}
            border-b border-gray-200 dark:border-slate-700
          `.trim().replace(/\s+/g, ' ')}
        >
          {header}
        </div>
      )}

      {/* Body Section */}
      <div className={paddingClasses[padding]}>
        {children}
      </div>

      {/* Footer Section */}
      {footer && (
        <div
          className={`
            ${paddingClasses[padding]}
            border-t border-gray-200 dark:border-slate-700
          `.trim().replace(/\s+/g, ' ')}
        >
          {footer}
        </div>
      )}
    </CardElement>
  );
};

CardComponent.displayName = 'Card';

/**
 * CardHeader - Convenience component for card headers
 * 
 * @example
 * ```tsx
 * <Card>
 *   <Card.Header>
 *     <h3>Title</h3>
 *     <button>Action</button>
 *   </Card.Header>
 *   <Card.Body>Content</Card.Body>
 * </Card>
 * ```
 */
export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`flex items-center justify-between ${className}`}>
    {children}
  </div>
);

CardHeader.displayName = 'CardHeader';

/**
 * CardTitle - Convenience component for card titles
 */
export const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <h3 className={`text-lg font-semibold text-gray-900 dark:text-slate-100 ${className}`}>
    {children}
  </h3>
);

CardTitle.displayName = 'CardTitle';

/**
 * CardDescription - Convenience component for card descriptions
 */
export const CardDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <p className={`text-sm text-gray-500 dark:text-slate-400 ${className}`}>
    {children}
  </p>
);

CardDescription.displayName = 'CardDescription';

/**
 * CardBody - Convenience component for card body
 */
export const CardBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={className}>
    {children}
  </div>
);

CardBody.displayName = 'CardBody';

/**
 * CardFooter - Convenience component for card footer
 */
export const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`flex items-center justify-end gap-2 ${className}`}>
    {children}
  </div>
);

CardFooter.displayName = 'CardFooter';

// Export Card with sub-components for dot notation
export const Card = Object.assign(CardComponent, {
  Header: CardHeader,
  Title: CardTitle,
  Description: CardDescription,
  Body: CardBody,
  Footer: CardFooter,
});

